import SkillCardTitle from './SkillCardTitle';
import React from 'react';
import { ArrowDownTrayIcon, ArrowPathIcon, CheckIcon, StarIcon } from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import { OnlineSkill } from '../../types/skill';
import { getLocalizedOnlineSkillSummary } from '../../utils/localizedOnlineSkillMetadata';
import { formatLocalizedCompactNumber } from '../../utils/localizedNumber';
import Tooltip from '../ui/Tooltip';
import SkillAvatar from './SkillAvatar';

interface OnlineSkillCardProps {
  skill: OnlineSkill;
  installStatus: 'idle' | 'installing' | 'installed';
  onInstall: (slug: string) => void;
  onOpenDetails: (skill: OnlineSkill) => void;
}

const OnlineSkillCard: React.FC<OnlineSkillCardProps> = ({ skill, installStatus, onInstall, onOpenDetails }) => {
  const localizedSummary = getLocalizedOnlineSkillSummary(skill);
  const language = i18nService.getLanguage();

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${i18nService.t('skillDetailsOpen')} · ${skill.displayName}`}
      onClick={() => onOpenDetails(skill)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenDetails(skill);
        }
      }}
      className="group flex min-h-[120px] cursor-pointer flex-col rounded-xl border border-claude-border bg-white p-3 shadow-[0_1px_2px_rgba(0,0,0,0.02)] outline-none transition-all duration-200 hover:border-claude-textSecondary/30 hover:shadow-[0_8px_24px_rgba(15,23,42,0.05)] focus-visible:border-claude-accent/60 focus-visible:ring-2 focus-visible:ring-claude-accent/20 dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:hover:border-claude-darkTextSecondary/40"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <SkillAvatar name={skill.displayName} className="h-8 w-8 rounded-lg [&_svg]:h-3.5 [&_svg]:w-3.5" />
          <SkillCardTitle name={skill.displayName} identifier={skill.slug} />
          <span className="flex-shrink-0 rounded-md bg-claude-surfaceMuted px-1.5 py-0.5 text-[10px] font-medium text-claude-textSecondary dark:bg-claude-darkSurfaceHover dark:text-claude-darkTextSecondary">
            v{skill.version}
          </span>
        </div>
        <div className="ml-1 flex-shrink-0">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onInstall(skill.slug);
            }}
            disabled={installStatus !== 'idle'}
            aria-label={installStatus === 'installed'
              ? i18nService.t('skillsOnlineInstalled')
              : installStatus === 'installing'
                ? i18nService.t('skillsOnlineInstalling')
                : i18nService.t('skillsOnlineInstall')}
            title={installStatus === 'installed'
              ? i18nService.t('skillsOnlineInstalled')
              : installStatus === 'installing'
                ? i18nService.t('skillsOnlineInstalling')
                : i18nService.t('skillsOnlineInstall')}
            className={`flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
              installStatus === 'installed'
                ? 'cursor-default text-claude-textSecondary/60 dark:text-claude-darkTextSecondary/60'
                : installStatus === 'installing'
                  ? 'cursor-wait text-claude-accent'
                  : 'text-claude-textSecondary hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText'
            }`}
          >
            {installStatus === 'installed' ? (
              <CheckIcon className="h-3.5 w-3.5" />
            ) : installStatus === 'installing' ? (
              <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </div>

      <Tooltip content={localizedSummary} position="bottom" maxWidth="360px" className="block w-full">
        <p className="mb-2 line-clamp-2 text-xs leading-[18px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
          {localizedSummary}
        </p>
      </Tooltip>

      <div className="mt-auto flex items-center gap-2 text-[10px] text-claude-textSecondary/80 dark:text-claude-darkTextSecondary/80">
        <span className="inline-flex items-center gap-0.5">
          <ArrowDownTrayIcon className="h-3 w-3" />
          {formatLocalizedCompactNumber(skill.downloads, language)}
        </span>
        <span className="inline-flex items-center gap-0.5">
          <StarIcon className="h-3 w-3" />
          {formatLocalizedCompactNumber(skill.stars, language)}
        </span>
      </div>
    </div>
  );
};

export default OnlineSkillCard;
